import { Component, Input, OnInit } from '@angular/core';
import { RecordCategory, RecordLine, RecordModel } from '../../record-model';
import * as Handlebars from 'handlebars';
import { LayoutService } from '../../layout.service';
import { FieldValueChangedArgs } from '../../field-value-changed-args';
import {
  ModuleStructure,
  Section,
  Values,
} from 'src/app/models/module-structure';

@Component({
  selector: 'app-list-section',
  templateUrl: './list-section.component.html',
  styleUrls: ['./list-section.component.less'],
})
export class ListSectionComponent implements OnInit {
  @Input() moduleStructure: ModuleStructure;
  @Input() section: Section;
  @Input() recordModel: RecordModel;
  @Input() defaultVals: Values;
  @Input() actionType = '';
  deletedLines: RecordLine[] = [];

  newRecordStr: string;
  lineCategory: RecordCategory;
  selectedIndex = 0;
  selectedLines: RecordLine[] = [];

  fields: any[];
  fieldGroups: any;
  thereIsCenter: boolean;
  fieldGroupCount: number;
  fieldGroupKeys: any[];
  fieldGroupLayouts: { [key: string]: { span: number; push: number; pull: number } };
  allowAdd: boolean;
  private titleTemplate: any;

  constructor(private layoutService: LayoutService) {}

  ngOnInit(): void {
    this.newRecordStr = 'New Record';
    this.lineCategory = this.getSectionCategory();
    this.titleTemplate = Handlebars.compile(
      this.section.AdditionalInformationParsed.TitleFormatHandlebars
    );

    this.lineCategory.Lines.forEach((line) => {
      line.Title = this.getTitle(line);
    });
    this.selectLine(this.selectedIndex);

    this.section.Fields = this.section.Fields.sort(
      (a, b) => a.SortOrder - b.SortOrder
    );

    this.prepareSectionFields();

    this.layoutService.fieldValueChanged.subscribe(
      (d: FieldValueChangedArgs) => {
        if (d.recordModel.FormGUID !== this.recordModel.FormGUID) {
          return;
        }

        if (d.sectionTagName === this.section.XmlTagName) {
          const line = this.lineCategory.Lines.find((l) => d.line === l.Id);
          line.Title = this.getTitle(line);
          if (line.Title === undefined || line.Title.trim() === '') {
            line.Title = '...';
          }
        }
      }
    );

    this.allowAdd =
      this.section.AdditionalInformationParsed.AllowAdd &&
      (this.section.AdditionalInformationParsed.AllowAddInEditMode ||
        this.recordModel.EventType === 0) &&
      this.actionType !== 'View';
  }

  getSectionCategory(): RecordCategory {
    if (this.recordModel !== undefined) {
      let recordCategory: RecordCategory = null;
      this.recordModel.Categories.forEach((category) => {
        if (category.Id === this.section.XmlTagName) {
          recordCategory = category;
        }
      });

      if (recordCategory == null) {
        recordCategory = {
          Id: this.section.XmlTagName,
          Lines: [],
        };
        this.recordModel.Categories.push(recordCategory);
      }
      return recordCategory;
    } else {
      return {
        Id: this.section.XmlTagName,
        Lines: [],
      };
    }
  }

  addNewCard() {
    let recordCategory: RecordCategory = null;
    this.recordModel.Categories.forEach((category) => {
      if (category.Id === this.section.XmlTagName) {
        recordCategory = category;
      }
    });

    if (recordCategory == null) {
      recordCategory = {
        Id: this.section.XmlTagName,
        Lines: [],
      };
      this.recordModel.Categories.push(recordCategory);
    }
    let recordLine: RecordLine = null;
    recordLine = {
      Id: recordCategory.Lines.length,
      Title: '...',
      Properties: [],
    };

    RecordModel.refineTheRecordModelLine(
      this.layoutService,
      this.section,
      recordLine,
      this.defaultVals
    );

    this.layoutService.lineAdded.emit({
      recordLine,
      moduleStructure: this.moduleStructure,
      section: this.section,
      recordModel: this.recordModel,
    });

    recordCategory.Lines.push(recordLine);
    this.lineCategory = recordCategory;

    this.selectLine(this.lineCategory.Lines.length - 1);

    for (const property of recordLine.Properties) {
      const field = this.section.Fields.find(
        (p) => p.PropertyName === property.Name
      );
      if (field.ParentProperties != null && field.ParentProperties !== '') {
        const queryIdToExecute = field.SQLStatementId;

        if (queryIdToExecute != null && queryIdToExecute !== 0) {
          this.layoutService.getFieldValueOnParentChange(
            this.recordModel,
            recordCategory,
            recordLine,
            property,
            queryIdToExecute,
            field,
            this.moduleStructure,
            true
          );
        }
      }
    }
  }

  deleteCard($event): void {
    const id = this.lineCategory.Lines[$event.index].Id;

    const lineToDelete = this.lineCategory.Lines.find((d) => d.Id === id);

    const lineToDeleteIndex = this.lineCategory.Lines.findIndex(
      (d) => d.Id === id
    );

    this.lineCategory.Lines = this.lineCategory.Lines.filter(
      (d) => d.Id !== id
    );
    if (lineToDeleteIndex < this.selectedIndex) {
      this.selectedIndex = this.selectedIndex - 1;
    } else if (this.selectedIndex >= this.lineCategory.Lines.length) {
      this.selectedIndex = Math.max(this.lineCategory.Lines.length - 1, 0);
    }

    for (let i = 0; i < this.lineCategory.Lines.length; i++) {
      this.lineCategory.Lines[i].Id = i;
    }

    const lineNumberValue = lineToDelete.Properties.find(
      (prop) => prop.Name === this.section.KeyPropertyName
    ).ValueAsObject;

    if (
      this.lineCategory.LineToDelete == null ||
      this.lineCategory.LineToDelete === undefined ||
      this.lineCategory.LineToDelete === ''
    ) {
      this.lineCategory.LineToDelete = lineNumberValue;
    } else {
      this.lineCategory.LineToDelete =
        this.lineCategory.LineToDelete + ',' + lineNumberValue;
    }

    this.lineCategory.KeyPropertyName = this.section.KeyPropertyName;
    this.lineCategory.PropertyNameInParent = this.section.PropertyNameInParent;
    this.deletedLines.push(lineToDelete);

    this.lineCategory.DeletedLines = this.deletedLines;

    if (this.lineCategory.Lines.length > 0) {
      RecordModel.refineTheRecordModelLine(
        this.layoutService,
        this.section,
        this.lineCategory.Lines[this.lineCategory.Lines.length - 1],
        this.defaultVals
      );
    }
    this.selectLine(this.selectedIndex);
  }

  getTitle(line) {
    let html = this.titleTemplate(line);
    if (html.length > 20) {
      html = html.substring(0, 20) + '...';
    }

    return html;
  }

  getSpanVal(fieldKey: number): number {
    if (fieldKey == 3 || this.fieldGroupCount === 1) {
      return 24;
    } else {
      if (this.thereIsCenter) {
        return 8;
      } else {
        return 12;
      }
    }
  }

  getPushVal(fieldKey: number): number {
    if (this.fieldGroupCount === 1) {
      return 0;
    } else {
      if (this.thereIsCenter && fieldKey == 2) {
        return 8;
      } else {
        return 0;
      }
    }
  }

  getPullVal(fieldKey: number): number {
    if (this.fieldGroupCount === 1) {
      return 0;
    } else {
      if (this.thereIsCenter && fieldKey === 4) {
        return 8;
      } else {
        return 0;
      }
    }
  }

  trackByLineId(_: number, line: RecordLine): number {
    return line.Id;
  }

  trackByFieldKey(_: number, fieldKey: any): any {
    return fieldKey;
  }

  trackByFieldId(_: number, field: any): number {
    return field.Id;
  }

  selectLine(index: number): void {
    this.selectedIndex = index;

    const line =
      this.lineCategory && this.lineCategory.Lines
        ? this.lineCategory.Lines[index]
        : null;
    this.selectedLines = line ? [line] : [];
  }

  prepareSectionFields() {
    this.fields = this.section.Fields.sort((a, b) => a.SortOrder - b.SortOrder);
    for (let i = 0; i < this.moduleStructure.module.Sections.length; i++) {
      let section = this.moduleStructure.module.Sections[i];
      if (section.XmlTagName == this.section.XmlTagName) {
        section = this.section;
        break;
      }
    }

    this.fieldGroups = this.fields.reduce(function (acc, obj) {
      let key = obj['PCAlignment'];
      if (key == 0) {
        key = 1;
      }
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});

    this.thereIsCenter =
      this.fields.filter((obj) => obj.PCAlignment === 4).length > 0;

    this.fieldGroupCount = Object.keys(this.fieldGroups).length;

    this.fieldGroupKeys = Object.keys(this.fieldGroups);
    this.fieldGroupLayouts = {};

    for (const key of this.fieldGroupKeys) {
      this.fieldGroups[key] = this.fieldGroups[key].sort((field1, field2) => {
        return this.layoutService.compareObjects(field1, field2, 'SortOrder');
      });
      this.fieldGroupLayouts[key] = {
        span: this.getSpanVal(Number(key)),
        push: this.getPushVal(Number(key)),
        pull: this.getPullVal(Number(key)),
      };
    }
  }
}
